import React, { useEffect, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { toast } from 'react-toastify';
import axios from 'axios'; // Ensure axios is imported
import { API_URL } from '../../constants';

ChartJS.register(ArcElement, Tooltip, Legend);

// Dummy formatCurrency for demonstration (if not available from appUtils)
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR', // Indian Rupee
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

// --- LIVE DATA FETCHING USING AXIOS ---
const fetchRevenueStats = async ({ userRole, userToken }) => {
    try {
        const response = await axios.get(`${API_URL}/dashboard/stats/revenue`, {
            headers: {
                Authorization: `Bearer ${userToken}`,
            },
        });

        if (response.data.success) {
            return response.data.data;
        } else {
            // If the API indicates failure but returns data.message
            throw new Error(response.data.message || 'Failed to fetch revenue stats');
        }
    } catch (error) {
        console.error('Revenue stats API error:', error);
        // Rethrow the error so the calling component can handle it (e.g., show a toast)
        throw error;
    }
};

const AdminRevenue = ({ userRole, userToken }) => {
    const [revenueStats, setRevenueStats] = useState({
        total: 0,
        thisMonth: 0,
        pending: 0,
        processed: 0,
        rejected: 0,
        portalEarning: 0,
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadRevenueStats = async () => {
            setLoading(true);
            try {
                const stats = await fetchRevenueStats({ userRole, userToken });
                setRevenueStats(stats);
            } catch (err) {
                toast.error('Failed to load revenue stats.'); // Using toast from react-toastify
                console.error('Error in loadRevenueStats:', err);
            } finally {
                setLoading(false);
            }
        };

        loadRevenueStats();
    }, [userRole, userToken]);

    // Calculate Vendor Gain (Total payouts to vendors)
    const vendorGain = revenueStats.total - revenueStats.portalEarning;

    // Chart Data for Payout Status
    const payoutChartData = {
        labels: ['Processed', 'Pending', 'Rejected'],
        datasets: [
            {
                data: [
                    revenueStats.processed,
                    revenueStats.pending,
                    revenueStats.rejected,
                ],
                backgroundColor: ['#28a745', '#ffc107', '#dc3545'], // Green, Yellow, Red
                borderColor: ['#fff', '#fff', '#fff'],
                borderWidth: 2,
            },
        ],
    };

    // Chart Data for Platform vs. Vendor Earnings
    const portalChartData = {
        labels: ['Platform Charges', 'Vendor Payouts'],
        datasets: [
            {
                data: [
                    revenueStats.portalEarning,
                    vendorGain, // Calculated vendor gain
                ],
                backgroundColor: ['#6610f2', '#6c757d'], // Purple for platform, Grey for vendor
                borderColor: ['#fff', '#fff'],
                borderWidth: 2,
            },
        ],
    };

    // Chart Options (reusable)
    const chartOptions = {
        cutout: '70%', // Makes it a Doughnut chart
        responsive: true,
        maintainAspectRatio: false, // Allows custom sizing
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    font: {
                        size: 14,
                        weight: 'bold'
                    },
                    color: '#333'
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed !== null) {
                            label += formatCurrency(context.parsed);
                        }
                        return label;
                    }
                }
            }
        },
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="ms-2">Loading revenue data...</p>
            </div>
        );
    }

    return (
        <div className="container my-lg-5 my-4">
            <h2 className="mb-5 text-center fw-bold text-primary">
                <i className="bi bi-bar-chart-fill me-3"></i>Revenue Dashboard
            </h2>

            {/* Top Row - Key Overview Cards */}
            <div className="row g-4 mb-5">
                <div className="col-lg-3 col-md-6">
                    <div className="card shadow-sm border-0 h-100 bg-primary text-white">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <i className="bi bi-cash-stack fs-1 me-3"></i>
                                <div>
                                    <h5 className="card-title fw-normal">Total Revenue</h5>
                                    <p className="card-text fs-4 fw-bold">{formatCurrency(revenueStats.total)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-3 col-md-6">
                    <div className="card shadow-sm border-0 h-100 bg-info text-white">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <i className="bi bi-calendar-check fs-1 me-3"></i>
                                <div>
                                    <h5 className="card-title fw-normal">This Month's Revenue</h5>
                                    <p className="card-text fs-4 fw-bold">{formatCurrency(revenueStats.thisMonth)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-3 col-md-6">
                    <div className="card shadow-sm border-0 h-100 bg-success text-white">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <i className="bi bi-people-fill fs-1 me-3"></i>
                                <div>
                                    <h5 className="card-title fw-normal">Vendor Gain</h5>
                                    <p className="card-text fs-4 fw-bold">{formatCurrency(vendorGain)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-3 col-md-6">
                    <div className="card shadow-sm border-0 h-100 bg-purple text-white">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <i className="bi bi-tags-fill fs-1 me-3"></i>
                                <div>
                                    <h5 className="card-title fw-normal">Platform Charges</h5>
                                    <p className="card-text fs-4 fw-bold">{formatCurrency(revenueStats.portalEarning)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Area - Payouts and Charts */}
            <div className="row g-4">
                <div className="col-lg-6">
                    <div className="card shadow-sm h-100">
                        <div className="card-body d-flex flex-column">
                            <h5 className="card-title fw-bold text-center mb-4">
                                <i className="bi bi-wallet-fill me-2"></i>Overall Payout Status
                            </h5>
                            <div className="d-flex justify-content-center align-items-center flex-grow-1" style={{ minHeight: '300px' }}>
                                <div style={{ width: '80%', maxWidth: '350px' }}>
                                    <Doughnut data={payoutChartData} options={chartOptions} />
                                </div>
                            </div>
                            <div className="row mt-4 text-center">
                                <div className="col-4">
                                    <h6 className="text-success fw-bold"><i className="bi bi-check-circle-fill"></i> Processed</h6>
                                    <p className="mb-0">{formatCurrency(revenueStats.processed)}</p>
                                </div>
                                <div className="col-4">
                                    <h6 className="text-warning fw-bold"><i className="bi bi-clock-fill"></i> Pending</h6>
                                    <p className="mb-0">{formatCurrency(revenueStats.pending)}</p>
                                </div>
                                <div className="col-4">
                                    <h6 className="text-danger fw-bold"><i className="bi bi-x-circle-fill"></i> Rejected</h6>
                                    <p className="mb-0">{formatCurrency(revenueStats.rejected)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-6">
                    <div className="card shadow-sm h-100">
                        <div className="card-body d-flex flex-column">
                            <h5 className="card-title fw-bold text-center mb-4">
                                <i className="bi bi-graph-up-arrow me-2"></i>Platform vs. Vendor Earnings
                            </h5>
                            <div className="d-flex justify-content-center align-items-center flex-grow-1" style={{ minHeight: '300px' }}>
                                <div style={{ width: '80%', maxWidth: '350px' }}>
                                    <Doughnut data={portalChartData} options={chartOptions} />
                                </div>
                            </div>
                            <div className="row mt-4 text-center">
                                <div className="col-6">
                                    <h6 className="text-purple fw-bold"><i className="bi bi-building"></i> Platform Charges</h6>
                                    <p className="mb-0">{formatCurrency(revenueStats.portalEarning)}</p>
                                </div>
                                <div className="col-6">
                                    <h6 className="text-secondary fw-bold"><i className="bi bi-person-fill"></i> Vendor Payouts</h6>
                                    <p className="mb-0">{formatCurrency(vendorGain)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminRevenue;